import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { LayoutDashboard, Building2, Users, CreditCard, LogOut, Shield, PlusCircle, Activity, Cpu } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';

const navItems = [
  { path: '/superadmin',               label: 'Dashboard',    icon: LayoutDashboard, exact: true },
  { path: '/superadmin/tenants',       label: 'Tenants',      icon: Building2 },
  { path: '/superadmin/users',         label: 'Users',        icon: Users },
  { path: '/superadmin/billing',       label: 'Billing',      icon: CreditCard },
  { path: '/superadmin/provisioning',  label: 'Provisioning', icon: PlusCircle },
  { path: '/superadmin/monitoring',    label: 'Monitoring',   icon: Activity },
  { path: '/superadmin/llm',           label: 'LLM Providers', icon: Cpu },
];

const SuperadminLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut } = useAuth();

  const isActive = (path: string, exact?: boolean) =>
    exact ? location.pathname === path : location.pathname.startsWith(path);

  return (
    <div className="flex h-screen bg-background overflow-hidden">
      {/* Sidebar */}
      <aside className="w-64 bg-card border-e border-border flex flex-col shrink-0">
        {/* Logo */}
        <div className="h-16 flex items-center gap-3 px-6 border-b border-border">
          <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
            <Shield className="w-4 h-4 text-primary-foreground" />
          </div>
          <div>
            <p className="text-sm font-semibold text-foreground">Superadmin</p>
            <p className="text-xs text-muted-foreground">Platform Control</p>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 p-4 space-y-1">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider px-3 mb-2">Monitor</p>
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path, item.exact);
            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className={cn(
                  'w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors',
                  active
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                )}
              >
                <Icon className="w-4 h-4 flex-shrink-0" />
                {item.label}
              </button>
            );
          })}
        </nav>

        {/* Footer */}
        <div className="p-4 border-t border-border space-y-2">
          <div className="px-3 py-2 rounded-lg bg-muted/50">
            <p className="text-xs font-medium text-foreground truncate">{user?.email}</p>
            <p className="text-xs text-muted-foreground">Platform Owner</p>
          </div>
          <button
            onClick={signOut}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
          >
            <LogOut className="w-4 h-4" />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-auto">
        <Outlet />
      </main>
    </div>
  );
};

export default SuperadminLayout;
